define('home/mixins/asChannelCard',[
    'jquery',
    'underscore',
    'backbone-marionette',
    'handlebars',

    'core/utils/auth',

    'home/models/Session',

    'home/views/ChannelCardPostView',
    'home/views/ChannelCardFooterView',
    'home/views/TopicTagCollectionView',
    'home/views/SpamAlertView',

    'home/mixins/withAllstarPopover',
    'home/mixins/withTruncate',
    'home/mixins/withSubviews',
], function (
    $,
    _,
    Marionette,
    Handlebars,

    auth,

    Session,

    ChannelCardPostView,
    ChannelCardFooterView,
    TopicTagCollectionView,
    SpamAlertView,

    withAllstarPopover,
    withTruncate,
    withSubviews
) {
    'use strict';

    var asChannelCard = {
        regions: {
            postsRegion: '[data-role=posts]',
            footerRegion: '[data-role=footer]',
            topicsRegion: '[data-role=topics]',
            cardAlertRegion: '[data-role=card-alert]',
        },

        events: {
            'click [data-action=menuitem-click]': 'handleMenuItemClick',
            'click [data-action=mark-approved]': 'markApproved',
        },

        className: function (existingClassName) {
            if (_.contains(this.options.filterVerbs, this.model.get('verb')))
                return 'hidden';

            if (_.isFunction(existingClassName))
                existingClassName = existingClassName.apply(this, _.rest(arguments));

            existingClassName = existingClassName || '';

            return [
                'card-wrap',
                'card--channel',
                existingClassName,
            ].join(' ');
        },

        initialize: function (_initialize, options) {
            options = options || {};
            _initialize.call(this, options);

            this.session = Session.get();
            this.channel = options.channel;
            this.hideImage = options.hideImage;
            this.listenTo(this.model.thread, 'change:content', this.render);
            this.numTitleLines = options.numTitleLines || 3;
            this.showTopics = this.model.thread.topics.length &&
                this.channel;
            this.isChannelFeed = options.isChannelFeed;

            var thread = this.model.thread;

            // Ensure computations for values that will be used in this view
            thread.getEditPermissions(this.session);
            thread.isAuthorSessionUser(this.session);
            thread.getModeratePermissions(this.session);

            this.listenTo(thread, {
                'change:userSubscription change:isDeleted change:canModerate change:isSpam': this.renderMenu,
                'change:canEdit': this.render,
                'change:isSpam': this.updateSpamIndicators,
            });
            this.listenTo(this.session, 'change:id', this.render);
        },

        serializeData: function (_serializeData) {
            var data = _serializeData.call(this);
            var thread = this.model.thread;
            var postsPreview = this.model.getPostsPreviewCollection();
            var friendlyUrl = thread.getFriendlySourceUrl() || thread.forum.getFriendlyUrl();
            var channel = this.channel || thread.forum.channel;
            var isAuthor = thread.get('isAuthor');

            // Only allow channel discussions to be edited until the create/edit form supports network discussions.
            var canEdit = thread.forum.channel && thread.get('canEdit');
            var canDelete = canEdit && (
                channel && auth.getFromCookie().staff ||
                _.contains(this.session.moderatedForums, thread.forum.id)
            );

            return _.defaults(data, {
                recentPostCount: postsPreview.length,
                discussionRoute: thread.getDiscussionRoute(this.channel),
                threadDescription: thread.getPlainDescription(),
                image: thread.getImage(),
                numTitleLines: this.numTitleLines,
                threadOverrides: this.model.getThreadOverrides(),
                forumFriendlyUrl: friendlyUrl,
                canEdit: canEdit,
                canDelete: canDelete,
                canModerate: thread.get('canModerate'),
                channel: channel,
                isChannelFeed: this.isChannelFeed,
                isSpam: thread.get('isSpam'),
                isAuthor: isAuthor,
            });
        },

        render: function (_render) {
            _render.call(this);

            if (this.$(this.postsRegion.selector).length)
                this.activateRegion(this.postsRegion, asChannelCard.createPostsView);

            if (this.showTopics)
                this.activateRegion(this.topicsRegion, asChannelCard.createTopicsView);

            this.activateRegion(this.footerRegion, asChannelCard.createFooter);

            this.updateSpamIndicators();

            return this;
        },

        /**
         * Updates the view to indicate that the thread is not spam.
         */
        removeSpamModTools: function () {
            this.cardAlertRegion.close();
            this.$el.removeClass('is-spam');
        },

        /**
         * Updates the view to indicate that the thread is spam.
         */
        showSpamModTools: function () {
            this.activateRegion(this.cardAlertRegion, asChannelCard.createSpamAlertView);
            this.$el.addClass('is-spam');
        },

        /**
         * Determines whether or not isSpam is true and updates the view appropriately.
         */
        updateSpamIndicators: function () {
            if (this.model.thread.get('isSpam')) {
                // Spam items can appear in feeds for moderators and authors. Only show the alert
                // to the moderators, since it contains the ability to approved as not-spam.
                this.model.thread.getModeratePermissions(this.session).then(_.bind(function (canModerate) {
                    if (canModerate)
                        this.showSpamModTools();
                    else
                        this.removeSpamModTools();
                }, this));
            } else {
                this.removeSpamModTools();
            }

            this.renderBadges();
        },

        renderBadges: function () {
            var data = this.serializeData();
            data = this.mixinTemplateHelpers(data);

            // Replace wrapper element because it comes as part of the partial. Done to make it easier
            // for many templates to use this partial without needing to also add the wrapper.
            this.$('[data-role=badges]').replaceWith(Handlebars.partials.badges(data));
        },

        renderMenu: function () {
            var data = this.serializeData();
            data = this.mixinTemplateHelpers(data);

            // Pass to the template whether or not the menu is currently open,
            // so that rerendering will maintain the open/closed state.
            data.menuOpen = this.$('.dropdown').hasClass('open');

            this.$('[data-role=menu]').replaceWith(Handlebars.partials.channelCardMenu(data));
        },

        createSpamAlertView: function () {
            return new SpamAlertView();
        },

        createPostsView: function () {
            var postsPreview = this.model.getPostsPreviewCollection();
            return new Marionette.CollectionView({
                itemView: ChannelCardPostView,
                collection: postsPreview,
            });
        },

        createTopicsView: function () {
            var topics = this.model.thread.topics;

            return new TopicTagCollectionView({
                collection: topics,
                channel: this.channel,
            });
        },

        _createFooter: function (el) {
            return new ChannelCardFooterView({
                el: el,
                model: this.model.thread,
                channel: this.channel,
            });
        },

        createFooter: function () {
            return asChannelCard._createFooter.call(this, this.footerRegion.el);
        },

        handleMenuItemClick: function (evt) {
            if (evt) {
                evt.preventDefault();

                // Prevent dropdown menu from closing when clicking inside
                evt.stopPropagation();

                var menuItem = $(evt.currentTarget).attr('data-menu-item');
                switch (menuItem) {
                case 'toggle-subscription':
                    this.toggleSubscription();
                    break;
                case 'toggle-follow-targetchannel':
                    this.toggleFollowTargetChannel();
                    break;
                case 'delete-thread':
                    this.model.thread.confirmDelete(true);
                    break;
                case 'restore-thread':
                    this.model.thread.restore();
                    break;
                case 'mark-spam':
                    this.model.thread.toggleIsSpam();
                    break;
                }
            }
        },

        markApproved: function () {
            this.model.thread.markAsSpam(false);
        },

        toggleSubscription: function () {
            this.model.thread.toggleSubscription();
        },

        toggleFollowTargetChannel: function () {
            var targetChannel = this.model.activities[0].targetChannel;
            if (targetChannel)
                targetChannel.toggleFollowed();
        },
    };

    return function () {
        withTruncate.apply(this, arguments);
        withSubviews.call(this);
        withAllstarPopover.call(this);

        this.regions = _.extend({}, asChannelCard.regions, this.regions);
        this.events = _.extend({}, asChannelCard.events, this.events);
        this.className = _.wrap(this.className, asChannelCard.className);
        this.initialize = _.wrap(this.initialize, asChannelCard.initialize);
        this.serializeData = _.wrap(this.serializeData, asChannelCard.serializeData);
        this.render = _.wrap(this.render, asChannelCard.render);

        _.extend(this, _.pick(asChannelCard,
            'handleMenuItemClick',
            'toggleSubscription',
            'toggleFollowTargetChannel',
            'renderMenu',
            'markApproved',
            'updateSpamIndicators',
            'showSpamModTools',
            'removeSpamModTools',
            'renderBadges'
        ));
    };
});

